import { StyleProp, StyleSheet, Text } from "react-native";
import { Dropdown } from "react-native-element-dropdown";
import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon, CloseIcon, Icon, LockIcon } from "ui/gluestack/icon";
import { requiredValidation } from "ui/utils/Validators";
import { View } from "ui/atoms/view";
import { ViewStyle } from "@expo/html-elements/build/primitives/View";
import { Button, ButtonIcon } from "ui/gluestack/button";

export interface SingleSelectDropdownProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  name: ControllerProps<TFieldValues>["name"];
  label?: string;
  placeholder?: string;
  helperText?: string;
  invalidMessage?: string;
  options: { value: number | string; label: string; disabled?: boolean }[];
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  hideSearch?: boolean;
  control: ControllerProps<TFieldValues>["control"];
  rules?: ControllerProps<TFieldValues>["rules"];
  className?: string;
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
  dropdownClassName?: StyleProp<ViewStyle>;
  canClear?: boolean;
  confirmSelectItem?: boolean;
  onConfirmSelectItem?: (item: any) => void;
}

export function FormControlledDropdown<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  label,
  placeholder,
  helperText,
  invalidMessage,
  options,
  isDisabled,
  isReadOnly,
  isRequired,
  isInvalid,
  hideSearch,
  control,
  rules,
  className,
  prefix,
  postfix,
  dropdownClassName,
  canClear,
  confirmSelectItem,
  onConfirmSelectItem,
}: SingleSelectDropdownProps<TFieldValues>) {
  const localRules = {
    required: isRequired ? requiredValidation(label ?? "Field") : undefined,
    ...rules,
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={localRules}
      render={({ fieldState: { error }, field: { value, onChange } }) => {
        const isFieldInvalid = isInvalid || !!error;

        const handleChange = (
          selectedValue: number | string | null,
          item?: { value: number | string; label: string; disabled?: boolean },
        ) => {
          if (item?.disabled) {
            return;
          }

          if (canClear && selectedValue === value) {
            onChange(null);
          } else {
            onChange(selectedValue);
          }
        };

        return (
          <FormControl
            isInvalid={isFieldInvalid}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
          >
            {label && (
              <Text className="text-sm font-medium mb-2 text-gray-600">
                {label}
                {!isRequired ? " (Optional)" : ""}
              </Text>
            )}
            <View
              className={`${className} ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
              {prefix}
              <Dropdown
                style={[
                  styles.dropdown,
                  isFieldInvalid && styles.invalidDropdown,
                  dropdownClassName,
                ]}
                placeholderStyle={styles.placeholderStyle}
                selectedTextStyle={styles.selectedTextStyle}
                inputSearchStyle={styles.inputSearchStyle}
                containerStyle={styles.containerStyle}
                iconStyle={styles.iconStyle}
                activeColor="#EBEFFF"
                search={!hideSearch}
                data={options}
                labelField="label"
                valueField="value"
                placeholder={placeholder || "Select item"}
                searchPlaceholder="Search..."
                value={value ?? null}
                confirmSelectItem={confirmSelectItem}
                onConfirmSelectItem={onConfirmSelectItem}
                onChange={(item) => handleChange(item.value, item)}
                disable={isDisabled}
                renderItem={(item) => (
                  <View
                    style={[styles.item, item.disabled && styles.disabledItem]}
                    pointerEvents={item.disabled ? "none" : "auto"}
                  >
                    <Text
                      style={[
                        styles.itemText,
                        item.disabled && styles.disabledText,
                      ]}
                    >
                      {item.label}
                    </Text>
                    {item.disabled && (
                      <Icon as={LockIcon} size="xs" color="#999" />
                    )}
                  </View>
                )}
                renderRightIcon={
                  isDisabled
                    ? () => <Icon as={LockIcon} color="#aaa" />
                    : value && canClear
                      ? () => (
                          <Button
                            variant="link"
                            onPress={() => handleChange(null)}
                          >
                            <ButtonIcon as={CloseIcon}></ButtonIcon>
                          </Button>
                        )
                      : undefined
                }
              />
              {postfix}
            </View>
            {helperText && (
              <FormControlHelper>
                <FormControlHelperText>{helperText}</FormControlHelperText>
              </FormControlHelper>
            )}

            {(invalidMessage || error?.message) && (
              <FormControlError>
                <FormControlErrorIcon
                  as={AlertCircleIcon}
                  className="text-error-500"
                  size="sm"
                />
                <FormControlErrorText className="text-error-500 text-sm">
                  {error?.message || invalidMessage}
                </FormControlErrorText>
              </FormControlError>
            )}
          </FormControl>
        );
      }}
    />
  );
}

const styles = StyleSheet.create({
  // eslint-disable-next-line react-native/no-color-literals
  containerStyle: {
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
  // eslint-disable-next-line react-native/no-color-literals
  disabledItem: {
    backgroundColor: "#f5f5f5",
    opacity: 0.6,
  },
  // eslint-disable-next-line react-native/no-color-literals
  disabledText: {
    color: "#999",
  },
  // eslint-disable-next-line react-native/no-color-literals
  dropdown: {
    backgroundColor: "transparent",
    borderColor: "#D1D5DB",
    borderRadius: 4,
    borderWidth: 1,
    height: 40,
    paddingLeft: 10,
    paddingRight: 10,
  },
  iconStyle: {
    height: 20,
    width: 20,
  },
  // eslint-disable-next-line react-native/no-color-literals
  inputSearchStyle: {
    color: "#000", // Hardcoded color
    fontSize: 16,
    height: 40,
  },
  // eslint-disable-next-line react-native/no-color-literals
  invalidDropdown: {
    borderColor: "#B91C1C",
    borderWidth: 2,
  },
  item: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 17,
  },
  // eslint-disable-next-line react-native/no-color-literals
  itemText: {
    color: "#000",
    flex: 1,
    fontSize: 16,
  },
  // eslint-disable-next-line react-native/no-color-literals
  placeholderStyle: {
    color: "#667085",
    fontSize: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  // eslint-disable-next-line react-native/no-color-literals
  selectedTextStyle: {
    color: "#475467",
    fontSize: 14,
  },
});
